import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Inbox,
  Search,
  X,
  XCircle,
} from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton, GlobalTextField } from "../../../global/forms/GlobalFormFields";
import { GlobalModal } from "../../../global/GlobalModal";
import {
  currentMonthParts,
  formatMonthLabel,
  isCurrentMonth,
  shiftMonth,
  toMonthInputValue,
} from "../../../global/dateNavUtils";
import {
  fetchLeaveManagerDashboard,
  approveLeaveApplication,
  rejectLeaveApplication,
} from "../api/attendanceOperationsApi";
import "../styles/attendance.css";
import "../styles/attendance-leave-manager.css";

function formatShortDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function countLeaveDays(from, to) {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const diff = Math.round((b - a) / 86400000) + 1;
  return diff > 0 ? diff : null;
}

function statusTone(status) {
  const s = String(status ?? "").toUpperCase();
  if (s === "APPROVED") return "approved";
  if (s === "REJECTED") return "rejected";
  return "pending";
}

const QUEUE_FILTERS = [
  { key: "ALL", label: "Inbox", icon: Inbox },
  { key: "PENDING", label: "Awaiting", icon: Clock3 },
  { key: "APPROVED", label: "Approved", icon: CheckCircle2 },
  { key: "REJECTED", label: "Declined", icon: XCircle },
];

const boot = currentMonthParts();

export default function AttendanceLeaveManagerPage() {
  const [year, setYear] = useState(boot.year);
  const [month, setMonth] = useState(boot.month);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [data, setData] = useState({ stats: {}, applications: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const monthInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchLeaveManagerDashboard({ year, month });
      setData(res);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setNameSearch("");
  }, [year, month]);

  const stats = data.stats ?? {};
  const periodLabel = formatMonthLabel(year, month);
  const viewingCurrent = isCurrentMonth(year, month);

  const statusFilteredApplications = useMemo(() => {
    const rows = [...(data.applications ?? [])].sort((a, b) =>
      String(a.leave_from).localeCompare(String(b.leave_from)),
    );
    if (statusFilter === "ALL") return rows;
    return rows.filter((row) => String(row.status).toUpperCase() === statusFilter);
  }, [data.applications, statusFilter]);

  const filteredApplications = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    if (!q) return statusFilteredApplications;
    return statusFilteredApplications.filter((row) =>
      String(row.user_name ?? "").toLowerCase().includes(q),
    );
  }, [statusFilteredApplications, nameSearch]);

  const searchTrimmed = nameSearch.trim();

  const queueCounts = useMemo(
    () => ({
      ALL: data.applications?.length ?? 0,
      PENDING: stats.pending ?? 0,
      APPROVED: stats.approved ?? 0,
      REJECTED: stats.rejected ?? 0,
    }),
    [data.applications, stats],
  );

  const openMonthPicker = () => {
    const input = monthInputRef.current;
    if (!input) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else input.click();
  };

  const handleMonthPick = (e) => {
    const raw = String(e.target.value ?? "").trim();
    if (!/^\d{4}-\d{2}$/.test(raw)) return;
    const [y, m] = raw.split("-").map(Number);
    setYear(y);
    setMonth(m);
  };

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await approveLeaveApplication(id);
      setSuccessMsg("Leave approved and email sent.");
      load();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      setErrorMsg("Rejection reason is required.");
      return;
    }
    setBusyId(rejectTarget.id);
    try {
      await rejectLeaveApplication(rejectTarget.id, rejectReason.trim());
      setRejectTarget(null);
      setRejectReason("");
      setSuccessMsg("Leave rejected and applicant notified.");
      load();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="att-shell att-lm-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />

      <header className="att-lm-command" aria-label="Leave manager">
        <div className="att-lm-command__left">
          <p className="att-lm-command__eyebrow">Approval desk</p>
          <h1 className="att-lm-command__title">Leave manager</h1>
        </div>

        <div className="att-lm-command__period">
          <button
            type="button"
            className="att-lm-command__arrow"
            onClick={() => {
              const n = shiftMonth(year, month, -1);
              setYear(n.year);
              setMonth(n.month);
            }}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="att-lm-command__month" onClick={openMonthPicker}>
            <CalendarRange size={15} aria-hidden />
            {periodLabel}
          </button>
          <input
            ref={monthInputRef}
            type="month"
            className="att-lm-command__month-input"
            value={toMonthInputValue(year, month)}
            onChange={handleMonthPick}
            tabIndex={-1}
            aria-hidden
          />
          <button
            type="button"
            className="att-lm-command__arrow"
            onClick={() => {
              const n = shiftMonth(year, month, 1);
              setYear(n.year);
              setMonth(n.month);
            }}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
          {!viewingCurrent ? (
            <button
              type="button"
              className="att-lm-command__today"
              onClick={() => {
                const t = currentMonthParts();
                setYear(t.year);
                setMonth(t.month);
              }}
            >
              This month
            </button>
          ) : null}
        </div>

        <dl className="att-lm-command__stats">
          <div className="att-lm-command__stat att-lm-command__stat--pending">
            <dt>Awaiting</dt>
            <dd>{loading ? "…" : stats.pending ?? 0}</dd>
          </div>
          <div className="att-lm-command__stat att-lm-command__stat--approved">
            <dt>Approved</dt>
            <dd>{loading ? "…" : stats.approved ?? 0}</dd>
          </div>
          <div className="att-lm-command__stat att-lm-command__stat--rejected">
            <dt>Declined</dt>
            <dd>{loading ? "…" : stats.rejected ?? 0}</dd>
          </div>
        </dl>
      </header>

      <div className="att-lm-desk">
        <aside className="att-lm-queue" aria-label="Queue filters">
          <p className="att-lm-queue__label">Queues</p>
          <ul className="att-lm-queue__list">
            {QUEUE_FILTERS.map((f) => {
              const Icon = f.icon;
              const active = statusFilter === f.key;
              return (
                <li key={f.key}>
                  <button
                    type="button"
                    className={`att-lm-queue__btn${active ? " att-lm-queue__btn--active" : ""}`}
                    onClick={() => setStatusFilter(f.key)}
                    aria-current={active ? "true" : undefined}
                  >
                    <Icon size={15} aria-hidden />
                    <span>{f.label}</span>
                    <em>{loading ? "…" : queueCounts[f.key] ?? 0}</em>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="att-lm-inbox" aria-labelledby="att-lm-inbox-title">
          <div className="att-lm-inbox__head">
            <div className="att-lm-inbox__head-text">
              <h2 id="att-lm-inbox-title">
                {QUEUE_FILTERS.find((f) => f.key === statusFilter)?.label ?? "Queue"}
              </h2>
              <p>
                {loading
                  ? "Loading applications…"
                  : `${filteredApplications.length} request${filteredApplications.length === 1 ? "" : "s"} · ${periodLabel}`}
              </p>
            </div>
            <label className="att-lm-search">
              <Search size={14} aria-hidden className="att-lm-search__icon" />
              <input
                type="search"
                className="att-lm-search__input"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Search by name…"
                aria-label="Search leave applications by user name"
              />
              {searchTrimmed ? (
                <button
                  type="button"
                  className="att-lm-search__clear"
                  onClick={() => setNameSearch("")}
                  aria-label="Clear name search"
                >
                  <X size={14} />
                </button>
              ) : null}
            </label>
          </div>

          {loading ? (
            <div className="att-lm-skeleton" aria-busy="true" />
          ) : filteredApplications.length === 0 ? (
            <div className="att-lm-empty">
              <Inbox size={30} strokeWidth={1.2} aria-hidden />
              <p>{searchTrimmed ? "No matching names" : "Queue is clear"}</p>
              <span>
                {searchTrimmed
                  ? `No leave requests for "${searchTrimmed}" in this queue for ${periodLabel}.`
                  : `No leave requests match this filter for ${periodLabel}.`}
              </span>
            </div>
          ) : (
            <ul className="att-lm-rows">
              {filteredApplications.map((row) => {
                const tone = statusTone(row.status);
                const days = countLeaveDays(row.leave_from, row.leave_to);
                const initial = String(row.user_name ?? "?").trim().charAt(0).toUpperCase() || "?";
                const isPending = tone === "pending";

                return (
                  <li key={row.id}>
                    <article className={`att-lm-row att-lm-row--${tone}`}>
                      <div className="att-lm-row__accent" aria-hidden />

                      <div className="att-lm-row__person">
                        <span className="att-lm-row__avatar">{initial}</span>
                        <div>
                          <strong>{row.user_name}</strong>
                          {row.user_email ? <span>{row.user_email}</span> : null}
                        </div>
                      </div>

                      <div className="att-lm-row__span">
                        <div className="att-lm-row__span-labels">
                          <span>{formatShortDate(row.leave_from)}</span>
                          <span>{formatShortDate(row.leave_to)}</span>
                        </div>
                        <div className="att-lm-row__span-track" aria-hidden>
                          <span className="att-lm-row__span-fill" />
                        </div>
                        {days ? <span className="att-lm-row__days">{days} days</span> : null}
                      </div>

                      <blockquote className="att-lm-row__reason">{row.reason}</blockquote>

                      <div className="att-lm-row__end">
                        {isPending ? (
                          <div className="att-lm-row__actions">
                            <GlobalButton
                              size="sm"
                              variant="primary"
                              disabled={busyId === row.id}
                              onClick={() => handleApprove(row.id)}
                            >
                              Approve
                            </GlobalButton>
                            <GlobalButton
                              size="sm"
                              variant="danger"
                              disabled={busyId === row.id}
                              onClick={() => {
                                setRejectTarget(row);
                                setRejectReason("");
                                setErrorMsg("");
                              }}
                            >
                              Reject
                            </GlobalButton>
                          </div>
                        ) : (
                          <p className="att-lm-row__verdict">
                            {row.status === "APPROVED" ? "Approved" : "Declined"}
                            {row.approver_name ? ` · ${row.approver_name}` : ""}
                            {row.approved_at
                              ? ` · ${new Date(row.approved_at).toLocaleDateString()}`
                              : ""}
                          </p>
                        )}
                        {row.rejection_reason ? (
                          <p className="att-lm-row__reject-note">{row.rejection_reason}</p>
                        ) : null}
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <GlobalModal
        open={Boolean(rejectTarget)}
        title="Reject leave?"
        onClose={() => !busyId && setRejectTarget(null)}
        footer={
          <>
            <GlobalButton variant="ghost" onClick={() => setRejectTarget(null)} disabled={Boolean(busyId)}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={confirmReject} disabled={Boolean(busyId)}>
              Reject
            </GlobalButton>
          </>
        }
      >
        <p>
          Reject leave for <strong>{rejectTarget?.user_name}</strong>?
        </p>
        <GlobalTextField
          label="Reason"
          value={rejectReason}
          onChange={setRejectReason}
          placeholder="Why is this rejected?"
          required
        />
      </GlobalModal>
    </div>
  );
}
